import { makeAssistantToolUI } from '@assistant-ui/react'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  addContributionToActive,
  openAsExploration,
} from '@/components/agent/tools/_shared/applyContribution'
import type { ToolActionHandlers } from '@/components/agent/tools/_shared/chat-tool-actions'
import { ToolCardActions } from '@/components/agent/tools/_shared/ToolCardActions'
import { FindShortestPathView } from '@/components/agent/tools/FindShortestPathView'
import { api } from '@/services/api'
import { useCockpitStore } from '@/stores/cockpit-store'
import { getOrCreateInvestigationsStore } from '@/stores/investigations-store'
import { emptyViewState, type GraphView } from '@/types/graph-view'
import { getHydratedNodeLabel } from '@/utils/entity-display'
import {
  type FindShortestPathArgs,
  type FindShortestPathResult,
  parseResult,
} from './find-shortest-path'
import { useToolCardActions } from './useToolCardActions'

type Status = { type: 'running' | 'complete' | 'incomplete' | string }

interface FindShortestPathRenderProps {
  args: FindShortestPathArgs
  result?: FindShortestPathResult | string
  status: Status
}

/**
 * Connected `find_shortest_path` tool card: parses the backend payload,
 * hydrates the path nodes into a GraphView for the canvas actions, resolves
 * the action footer from the chat context, and hands pure data to
 * FindShortestPathView.
 */
function FindShortestPathRender({ args, result, status }: FindShortestPathRenderProps) {
  const navigate = useNavigate()
  const { caseId } = useParams<{ caseId: string }>()

  const running = status.type === 'running'
  const parsed = useMemo(() => parseResult(result), [result])
  const errored = parsed?.status === 'error' || status.type === 'incomplete'

  const steps = parsed?.steps ?? []
  const subject =
    parsed?.source && parsed.target ? `${parsed.source.name} → ${parsed.target.name}` : undefined
  const entityIds = parsed?.hydrate?.node_ids ?? []

  const handlers = useMemo<ToolActionHandlers>(() => {
    if (parsed?.status !== 'ok' || !parsed.hydrate || !caseId) return {}
    const suggestedTitle = parsed.hydrate.label ?? `Shortest path: ${subject ?? '?'}`

    const hydrateGraphView = async (): Promise<GraphView | null> => {
      if (!parsed.hydrate || !caseId) return null
      try {
        const data = await api.graph.hydrateGraph(caseId, {
          node_ids: parsed.hydrate.node_ids,
          relationship_ids: parsed.hydrate.relationship_ids,
        })
        return {
          meta: {
            source: 'assistant',
            createdAt: new Date().toISOString(),
            caseId,
            label: suggestedTitle,
          },
          nodes: data.nodes.map(n => ({
            id: n.id,
            labels: n.labels,
            typeLabel: n.labels[0],
            // Walk the documented display-name fallback chain (incl. the
            // schema-stamped `_display_name`) so canvas chips don't fall back
            // to "Default" when the hydrate response leaves `n.name` empty.
            name: getHydratedNodeLabel(n),
            properties: n.properties ?? {},
          })),
          edges: data.relationships.map(r => ({
            id: r.id,
            type: r.type,
            source: r.source,
            target: r.target,
            properties: r.properties ?? {},
          })),
          view: emptyViewState(),
        }
      } catch (e) {
        console.error('[ShortestPath] hydrate failed:', e)
        toast.error('Failed to load path on graph')
        return null
      }
    }

    const pathHighlightIds = () => {
      const nodeIds = steps.map(s => s.node.element_id)
      const edgeIds = steps.filter(s => s.relationship).map(s => s.relationship!.element_id)
      return { nodeIds, edgeIds }
    }

    const runAndNavigate = async (apply: 'explore' | 'add') => {
      const view = await hydrateGraphView()
      if (!view) return
      const store = getOrCreateInvestigationsStore({ caseId })
      if (apply === 'explore') {
        openAsExploration({ store, view, label: suggestedTitle })
      } else {
        addContributionToActive({ store, view, label: suggestedTitle })
      }
      // Intentionally no pathHighlight here — "Show on graph" and "Add to
      // investigation" land the data on canvas; highlight styling is the
      // exclusive job of the Highlight action.
      navigate(`/cases/${caseId}/graph`)
    }

    const highlightHandler = () => {
      const { nodeIds, edgeIds } = pathHighlightIds()
      useCockpitStore.getState().setPathHighlight(nodeIds, edgeIds)
      navigate(`/cases/${caseId}/graph`)
    }

    const exploreHandler = () => void runAndNavigate('explore')

    return {
      // Same handler for both — picker chooses the label based on whether an
      // active viewpoint exists ("Show on graph" vs "Show separately").
      showOnGraph: exploreHandler,
      showSeparately: exploreHandler,
      addToGraph: () => void runAndNavigate('add'),
      highlight: highlightHandler,
      saveAsInvestigation: async () => {
        const view = await hydrateGraphView()
        if (!view) return
        const store = getOrCreateInvestigationsStore({ caseId })
        store.getState().setTransient(view, suggestedTitle)
        await store.getState().saveTransient(suggestedTitle)
        navigate(`/cases/${caseId}/graph`)
      },
    }
  }, [parsed, caseId, subject, steps, navigate])

  const actionsModel = useToolCardActions(entityIds, handlers)
  const actions = actionsModel ? <ToolCardActions model={actionsModel} handlers={handlers} /> : null

  return (
    <FindShortestPathView
      args={args}
      parsed={parsed}
      running={running}
      errored={errored}
      actions={actions}
    />
  )
}

export const FindShortestPathToolUI = makeAssistantToolUI<
  FindShortestPathArgs,
  FindShortestPathResult | string
>({
  toolName: 'find_shortest_path',
  render: FindShortestPathRender,
})
